import type { StepExtended } from '@epilot/journey-logic-commons'
import type {
  Block,
  BlockId
} from '@epilot/journey-logic-commons/src/types/next'
import type { CoreActions, JsonFormsCore } from '@jsonforms/core'
import { JsonForms } from '@jsonforms/react'
import { once } from 'radashi'
import { memo, useEffect, useMemo, type ComponentProps } from 'react'
import { shallow } from 'zustand/shallow'

import {
  useAdditionalErrorsFromState,
  useJourneyContext
} from '../../../blocks-renderers'
import { useAppStoreSelector } from '../../../hooks/useAppStoreSelector'
import { selectHiddenBlocks } from '../../../store/selectors/runtime'
import { TIME_TO_FIRST_RENDER_METRIC } from '../../../utils/config'
import { initAJV } from '../../../utils/initAJV'
import { getRenderers } from '../../../utils/renderers'
import { endTimer } from '../../../utils/timers'

/**
 * Ensures the underlying metric is stopped only once.
 */
const endTimeToFirstRender = once(() => endTimer(TIME_TO_FIRST_RENDER_METRIC))

/**
 * Creates a map of hidden block ids by their names.
 */
const getHiddenBlocksByName = (hiddenBlocksMap: Record<BlockId, Block>) => {
  return Object.keys(hiddenBlocksMap).reduce(
    (acc, blockId) => {
      const block = hiddenBlocksMap[blockId]

      if (block?.name) {
        acc[block.name] = blockId
      }

      return acc
    },
    {} as Record<string, BlockId>
  )
}

/**
 * TODO: Cross-check how this behaves with the old engine
 */
const middlewareFactory =
  (hiddenBlocksMap: Record<BlockId, Block>) =>
  (
    state: JsonFormsCore,
    action: CoreActions,
    defaultReducer: (state: JsonFormsCore, action: CoreActions) => JsonFormsCore
  ) => {
    const newState = defaultReducer(state, action)

    const schemaProperties = state.schema.properties
    const schemasWithDefault: string[] = []

    for (const key in schemaProperties) {
      if (Object.keys(schemaProperties[key]).includes('default')) {
        schemasWithDefault.push(key)
      }
    }

    if (schemasWithDefault.length) {
      const uischemaElements = (state.uischema as any).elements.flat()

      for (const element of uischemaElements) {
        // keys of hidden blocks
        const keys: string[] = []

        if (
          element.type === 'GroupLayout' &&
          element.options.display === false
        ) {
          for (const e of element.elements) {
            keys.push(e.scope.split('/')[2])
          }
        } else if (element.options.display === false) {
          keys.push(element.scope.split('/')[2])
        }

        for (const key of keys) {
          if (schemasWithDefault.includes(key)) {
            newState.data[key] = undefined
          }
        }
      }
    }

    if (newState.errors?.length) {
      // create simple map to lookup hidden blocks by name
      const hiddenBlocksByName = getHiddenBlocksByName(hiddenBlocksMap)

      // Check if any of the blocks containing a "required" error, are hidden. If so, filter them out.
      const filteredErrors = newState.errors?.filter((error) => {
        const blockName = error.params.missingProperty

        return !(
          error.keyword === 'required' && !!hiddenBlocksByName[blockName]
        )
      })

      newState.errors = filteredErrors
    }

    return newState
  }

type StepContentProps = Omit<
  ComponentProps<typeof JsonForms>,
  'additionalErrors' | 'ajv' | 'middleware' | 'renderers' | 'validationMode'
> & {
  /** Which Step to render the content */
  step: StepExtended
}

const StepContent = memo(({ step, ...formProps }: StepContentProps) => {
  const ajv = initAJV()
  // TODO: Expensive operation, move to AppStore
  const validationMode = useJourneyContext().context.errorValidationMode

  const hiddenBlocksMap = useAppStoreSelector(
    (state) => selectHiddenBlocks(state, step.stepId),
    shallow
  )

  const additionalErrors = useAdditionalErrorsFromState(
    formProps?.data,
    validationMode,
    step
  )

  const middleware = useMemo(() => {
    return middlewareFactory(hiddenBlocksMap ?? {})
  }, [hiddenBlocksMap])

  /**
   * Marks the end of the first render.
   * NOTE: This should only be called once, after the component is mounted the first time.
   */
  useEffect(() => void endTimeToFirstRender(), [])

  return (
    <JsonForms
      additionalErrors={additionalErrors}
      ajv={ajv}
      middleware={middleware}
      renderers={getRenderers()}
      validationMode={validationMode}
      {...formProps}
    />
  )
})

StepContent.displayName = 'StepContent'

export default StepContent // for lazy loading
